import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useIntl } from 'ha/i18n';
import { TabPanel, Grid, units } from '@hbf/dsl';
import { Label, StyledTab, StyledTabs } from 'ha/components/StyledTabs';

import { HowItWorks } from 'ha/components/HowItWorks';
import { tenantVps, landlordVps } from 'ha/components/HowItWorks/constants';
import { Testimonial } from './Testimonial';

const useStyles = makeStyles()(theme => ({
  root: {
    marginTop: units.rem(2.5),

    [theme.breakpoints.up('md')]: {
      marginTop: units.rem(5),
    },
  },
  tabs: {
    padding: units.yx(0, 0.5),
    marginBottom: units.rem(4),
  },
  activeTab: {
    color: theme.palette.primary.main,
  },
  howItWorksSection: {
    marginBottom: units.rem(3.75),

    [theme.breakpoints.up('md')]: {
      marginBottom: units.rem(6.25),
    },
  },
}));

const InfoSectionInternal: React.FC = () => {
  const { T, urlResolver } = useIntl();
  const { classes } = useStyles();
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const ctaButtonText = T('Learn more');

  const handleTabChange = (
    _: React.ChangeEvent<HTMLSelectElement>,
    newTabIndex: number,
  ) => {
    setCurrentTabIndex(newTabIndex);
  };

  const isTenantTabSelected = currentTabIndex === 0;
  const tabs = React.useMemo(
    () => [
      {
        vps: tenantVps,
        ctaUrl: urlResolver.getRenting(),
        dataTestLocator: 'ForTenants',
      },
      {
        vps: landlordVps,
        ctaUrl: urlResolver.getRentingOut(),
        dataTestLocator: 'ForLandlords',
      },
    ],
    [urlResolver],
  );

  return (
    <div className={classes.root}>
      <Grid container justifyContent="center" className={classes.tabs}>
        <StyledTabs value={currentTabIndex} onChange={handleTabChange}>
          <StyledTab
            label={<Label>{T('home_page_info_section_for_tenants')}</Label>}
            classes={{ selected: classes.activeTab }}
            data-test-locator="Home InfoSection StyledTab ForTenants"
          />
          <StyledTab
            label={<Label>{T('home_page_info_section_for_landlords')}</Label>}
            classes={{ selected: classes.activeTab }}
            data-test-locator="Home InfoSection StyledTab ForLandlords"
          />
        </StyledTabs>
      </Grid>
      {tabs.map((tab, index) => (
        <TabPanel
          key={tab.dataTestLocator}
          value={currentTabIndex}
          index={index}
          data-test-locator={`Home InfoSection TabPanel ${tab.dataTestLocator}`}
        >
          <div className={classes.howItWorksSection}>
            <HowItWorks
              vps={tab.vps}
              ctaUrl={tab.ctaUrl}
              ctaButtonText={ctaButtonText}
              dataTestLocator={`Home InfoSection HowItWorks  ${tab.dataTestLocator}`}
            />
          </div>
        </TabPanel>
      ))}
      <Testimonial
        ctaUrl={
          isTenantTabSelected
            ? undefined
            : urlResolver.getBecomeAnAdvertiserUrl()
        }
        dataTestLocator={`Home InfoSection Testimonial ${
          isTenantTabSelected ? 'ForTenants' : 'ForLandlords'
        }`}
        isTenant={isTenantTabSelected}
      />
    </div>
  );
};

const InfoSection = React.memo(InfoSectionInternal);
export { InfoSection };
